import { useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import type { EmployeeRole } from "@/utils/permissions";

type AddUserModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onAddUser: (name: string, email: string, role: EmployeeRole) => Promise<void>;
  existingEmails?: string[];
};

const AddUserModal = ({ isOpen, onClose, onAddUser, existingEmails = [] }: AddUserModalProps) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<EmployeeRole>("basic_employee");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async () => {
    if (!name.trim() || !email.trim()) {
      toast.error("Campos requeridos", {
        description: "Por favor completa todos los campos",
      });
      return;
    }

    if (!validateEmail(email)) {
      toast.error("Email inválido", {
        description: "Por favor ingresa un email válido",
      });
      return;
    }

    // Check if email already exists
    if (existingEmails.some((existingEmail) => existingEmail.toLowerCase() === email.trim().toLowerCase())) {
      toast.error("Email ya existe", {
        description: "Ya existe un usuario con este email en la organización",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await onAddUser(name.trim(), email.trim(), role);

      toast.success("Usuario agregado", {
        description: "El usuario se agregó correctamente a la organización",
      });

      // Reset form and close modal
      setName("");
      setEmail("");
      setRole("basic_employee");
      onClose();
    } catch (error: any) {
      toast.error("Error al agregar usuario", {
        description: error.message || "No se pudo agregar el usuario. Intenta nuevamente.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setName("");
      setEmail("");
      setRole("basic_employee");
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="h-full max-h-full md:h-auto md:max-h-[85vh] md:max-w-md">
        <DialogHeader>
          <DialogTitle>Agregar nuevo usuario</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <p className="text-sm text-gray-600">
            Agrega un nuevo miembro a tu organización. Se creará una cuenta en el sistema.
          </p>
          <div className="flex flex-col gap-2">
            <Label className="text-sm font-medium text-gray-700">
              Nombre completo
            </Label>
            <Input
              placeholder="Ingresa el nombre completo"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isSubmitting}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label className="text-sm font-medium text-gray-700">
              Email
            </Label>
            <Input
              placeholder="Ingresa el email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isSubmitting}
            />
            <p className="text-xs text-gray-500">
              El usuario recibirá un correo para configurar su cuenta
            </p>
          </div>
          <div className="flex flex-col gap-2">
            <Label className="text-sm font-medium text-gray-700">
              Rol
            </Label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as EmployeeRole)}
              disabled={isSubmitting}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option value="restricted_employee">Empleado Restringido</option>
              <option value="basic_employee">Empleado Básico</option>
              <option value="admin_employee">Empleado Administrador</option>
            </select>
            <p className="text-xs text-gray-500">
              Los administradores pueden gestionar usuarios de la organización
            </p>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={handleClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || !name.trim() || !email.trim()}
          >
            {isSubmitting ? "Agregando..." : "Agregar usuario"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddUserModal;
